/**
 * @file Hook: useGameState
 * @description A custom hook to manage the global state of the game with multi-level support.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCaseData,
  selectCurrentLocation,
  selectCurrentLocationIndex,
  selectEvidenceBucket,
  selectGameStatus,
  selectWrongClicks,
  selectWrongClickTimestamps,
} from '@/src/store/game/gameSelectors';
import {
  handleGameError,
  initializeGame,
  initializeLocation,
  initializeLocationEvidenceBucket,
  resetGameState,
  setCurrentLocationIndex,
  setGameStatus,
  setLoadingMessage,
  setWrongClick,
} from '@/src/store/game/gameSlice';
import {
  selectCumulativeScore,
  selectLevelResults,
} from '@/src/store/levelResults/levelResultsSelectors';
import {
  addLevelResult,
  resetLevelResults,
  setLastGameResult,
} from '@/src/store/levelResults/levelResultsSlice';
import { resetOverlays } from '@/src/store/overlay/overlaySlice';
import {
  selectBonusStreak,
  selectEvidenceFinds,
  selectHighestSpeedStreak,
} from '@/src/store/scoring/scoringSelectors';
import {
  breakBonusStreak,
  continueBonusStreak,
  resetBonusStreak,
  resetScoring,
  setCurrentIPScore,
  startBonusStreak,
} from '@/src/store/scoring/scoringSlice';
import { BONUS_STREAK_CONFIG } from '../constants/bonusStreakConfig';
import { DEFAULT_TIME_TARGETS } from '../constants/ipScoringConfig';
import { analytics } from '../services/analyticsService';
import { loadCaseData } from '../services/dataService';
import { saveService } from '../services/gameSaveService';
import {
  type GameConfig,
  type GameResult,
  GameStatus,
  type LevelResult,
} from '../types';
import type { EvidenceFind, IPScoreBreakdown } from '../types/scoring';
import {
  calculateAccuracyMultiplier,
  calculateCasePins,
  calculateIPScore,
  calculatePaceMultiplier,
  getStreakMultiplier,
} from '../utils/ipScoringSystem';
import { useStreakTimer } from './useStreakTimer';

/**
 * Manages the overall state and logic of the game with multi-level support.
 * @returns An object containing the game state and functions to manipulate it.
 */
export const useGameState = (gameConfig?: GameConfig | null) => {
  const dispatch = useDispatch();
  const caseData = useSelector(selectCaseData);
  const currentLocation = useSelector(selectCurrentLocation);
  const currentLocationIndex = useSelector(selectCurrentLocationIndex);

  const wrongClickTimestamps = useSelector(selectWrongClickTimestamps);
  const wrongClicks = useSelector(selectWrongClicks);

  const cumulativeScore = useSelector(selectCumulativeScore);
  const levelResults = useSelector(selectLevelResults);

  const evidenceFinds = useSelector(selectEvidenceFinds);
  const evidenceBucket = useSelector(selectEvidenceBucket);

  const gameStatus = useSelector(selectGameStatus);

  // Bonus streak selectors
  const bonusStreak = useSelector(selectBonusStreak);
  const highestSpeedStreak = useSelector(selectHighestSpeedStreak);

  // Timer hook for bonus streak
  const { startTimer, stopTimer } = useStreakTimer();

  // Game timer reference
  const gameStartTimeRef = useRef<number>(0);

  /**
   * Returns to the title screen
   */
  const returnToTitle = useCallback(() => {
    dispatch(resetGameState());
  }, [dispatch]);

  /**
   * Moves to a new location for gameplay
   */
  const moveToLocation = useCallback(
    (locationIndex: number, skipStatusChange = false) => {
      if (
        !caseData ||
        !caseData.locations ||
        locationIndex >= caseData.locations.length
      ) {
        return;
      }

      dispatch(initializeLocationEvidenceBucket());

      // Reset gameplay tracking for scoring, streaks
      dispatch(resetBonusStreak());
      dispatch(resetScoring());

      dispatch(
        initializeLocation({
          skipStatusChange,
        }),
      );

      // Start with countdown for this location (unless skipping for video/narrative)
      if (!skipStatusChange) {
        dispatch(setGameStatus(GameStatus.GAMEPLAY));
      }

      // Reset game timer reference
      gameStartTimeRef.current = Date.now();
    },
    [caseData, dispatch],
  );

  /**
   * Starts a new game with the specified case ID
   */
  const startNewGame = useCallback(
    (caseId: string, _isDevMode: boolean = false) => {
      const start = async () => {
        dispatch(setGameStatus(GameStatus.GENERATING));
        dispatch(setLoadingMessage('Loading case data...'));

        try {
          const newCaseData = await loadCaseData(caseId);

          if (!newCaseData) {
            throw new Error(`Case with ID "${caseId}" not found.`);
          }

          // Reset scoring state when starting a new case
          dispatch(resetScoring());
          dispatch(resetLevelResults());
          dispatch(resetOverlays());

          // EXPERIMENT: Enable sequential scene unlocking for all locations
          const experimentCaseData = {
            ...newCaseData,
            locations: newCaseData.locations.map((location) => ({
              ...location,
              gameConfig: {
                ...location.gameConfig,
                sequentialSceneUnlocking: true,
              },
            })),
          };

          dispatch(initializeGame(experimentCaseData));
        } catch (err) {
          console.error('❌ Error loading case data:', err);
          let errorMessage = 'An unknown error occurred.';
          if (err instanceof Error) {
            const lowerCaseMessage = err.message.toLowerCase();
            if (
              lowerCaseMessage.includes('resource_exhausted') ||
              lowerCaseMessage.includes('quota')
            ) {
              errorMessage =
                'The network is busy. Too many requests are being made. Please wait a moment and try again.';
            } else {
              errorMessage = err.message;
            }
          }
          dispatch(handleGameError(errorMessage));
        }
      };

      start();
    },
    [dispatch],
  );

  /**
   * Advances to the next location
   */
  const advanceToNextLocation = useCallback(() => {
    if (!caseData) return;

    const nextLocationIndex = currentLocationIndex + 1;

    // Check if there are more locations
    if (nextLocationIndex >= caseData.locations.length) {
      // No more locations, end the game
      dispatch(setGameStatus(GameStatus.FINAL_SUMMARY));
    } else {
      // Move to the next location
      dispatch(setCurrentLocationIndex(nextLocationIndex));
      moveToLocation(nextLocationIndex, true); // Skip status change for video/narrative
    }
  }, [
    caseData,
    currentLocationIndex, // Move to the next location
    dispatch,
    moveToLocation,
  ]);

  /**
   * Checks if any new scenes should be unlocked based on the found item
   */
  const checkAndUnlockScenes = useCallback(
    (foundItemId: string) => {
      if (!evidenceBucket) return [];

      if (!currentLocation) return [];

      const newlyUnlockedScenes: string[] = [];

      // Check each scene to see if this item unlocks it
      currentLocation.scenes.forEach((scene) => {
        if (
          scene.unlockRequiredItemId === foundItemId &&
          !evidenceBucket.unlockedSceneIds.includes(scene.id)
        ) {
          newlyUnlockedScenes.push(scene.id);
        }
      });

      return newlyUnlockedScenes;
    },
    [evidenceBucket, currentLocation],
  );

  /**
   * Updates the current IP score based on evidence finds and wrong clicks
   */
  const updateCurrentIPScore = useCallback(() => {
    if (!caseData || !evidenceBucket) return;

    // Get the level configuration and actual required evidence
    const requiredCount =
      currentLocation?.gameConfig?.minimumEvidence ??
      gameConfig.minimumEvidence;

    // Calculate IP score with configurable time threshold
    const streakTimeThreshold =
      gameConfig?.streakTimeThreshold ??
      BONUS_STREAK_CONFIG.DEFAULT_STREAK_TIMEOUT;

    // Update current IP score
    dispatch(
      setCurrentIPScore({
        wrongClickTimestamps,
        requiredCount,
        caseId: caseData.caseId,
        streakTimeThreshold,
      }),
    );
  }, [
    caseData,
    evidenceBucket,
    wrongClickTimestamps,
    currentLocation,
    gameConfig,
    dispatch,
  ]);

  /**
   * Updates the bonus streak state based on a new evidence find
   */
  const updateBonusStreak = useCallback(
    (newFind: EvidenceFind) => {
      // Check if this find has a time efficiency bonus
      if (newFind.baseTimeEfficiencyBonus > 0) {
        if (!bonusStreak || !bonusStreak.isActive) {
          // Start new streak
          dispatch(
            startBonusStreak({
              baseTimeEfficiencyBonus: newFind.baseTimeEfficiencyBonus,
            }),
          );

          // Analytics tracking
          analytics.trackBonusCollected(
            'Speed Streak',
            `+${newFind.baseTimeEfficiencyBonus}s (x1)`,
          );
        } else {
          // Continue streak
          dispatch(
            continueBonusStreak({
              baseTimeEfficiencyBonus: newFind.baseTimeEfficiencyBonus,
            }),
          );

          // Analytics tracking with multiplier
          analytics.trackBonusCollected(
            'Speed Streak',
            `+${newFind.baseTimeEfficiencyBonus}s (x${bonusStreak.currentMultiplier})`,
          );
        }

        // Start/restart timer
        startTimer();
      } else {
        // Break streak
        dispatch(breakBonusStreak());
        stopTimer();
      }
    },
    [bonusStreak, dispatch, startTimer, stopTimer],
  );

  /**
   * Breaks the current streak
   */
  const breakStreak = useCallback(() => {
    stopTimer();
    dispatch(breakBonusStreak());
  }, [dispatch, stopTimer]);

  /**
   * Records a wrong click with timestamp
   */
  const recordWrongClick = useCallback(() => {
    dispatch(setWrongClick());

    // Update current IP score
    updateCurrentIPScore();

    // Break bonus streak on wrong click
    breakStreak();
  }, [
    updateCurrentIPScore, // Break bonus streak on wrong click
    breakStreak,
    dispatch,
  ]);

  /**
   * Calculates and sets the level summary
   */
  const calculateAndSetLevelSummary = useCallback(
    (timeRemaining: number) => {
      if (!caseData || !evidenceBucket) return null;

      if (!currentLocation) return null;

      // Get the actual required evidence: location-level override > global config > level config fallback
      const actualRequiredEvidence =
        currentLocation.gameConfig?.minimumEvidence != null
          ? currentLocation.gameConfig.minimumEvidence
          : gameConfig?.minimumEvidence != null
            ? gameConfig.minimumEvidence
            : 0;

      // Use the evidence bucket's found evidence
      const foundEvidenceWithConclusions = evidenceBucket.foundEvidence.map(
        (item) => {
          // Find the original item to get its conclusion
          const originalItem = evidenceBucket.evidence.find(
            (i) => i.id === item.id,
          );
          return {
            ...item,
            conclusion: originalItem?.conclusion,
          };
        },
      );

      const totalClicks =
        evidenceBucket.foundEvidence.length + wrongClicks || 1;
      const accuracy = Math.round(
        (evidenceBucket.foundEvidence.length / totalClicks) * 100,
      );

      // Get time targets from caseData or use defaults
      const parTime = caseData.parTime ?? DEFAULT_TIME_TARGETS.parTime;
      const aceTime = caseData.aceTime ?? DEFAULT_TIME_TARGETS.aceTime;

      // Calculate total completion time from evidence finds
      let totalCompletionTime = 0;
      let firstFindTimestamp: number | undefined;
      let lastFindTimestamp: number | undefined;
      if (evidenceFinds.length > 0) {
        const timestamps = evidenceFinds.map((f) => f.timeWhenFound);
        firstFindTimestamp = Math.min(...timestamps);
        lastFindTimestamp = Math.max(...timestamps);
        totalCompletionTime = (lastFindTimestamp - firstFindTimestamp) / 1000;
      }

      const ipBreakdown = calculateIPScore(
        evidenceFinds,
        wrongClickTimestamps,
        actualRequiredEvidence,
        caseData.caseId,
        totalCompletionTime,
        parTime,
        aceTime,
      );

      // Calculate time bonus for display purposes (not used in IP scoring)
      const timeBonus = timeRemaining * 10;

      // Use the IP score as the final score
      // Note: Wrong clicks have NO direct score penalty in the IP system
      // They only affect score indirectly through:
      // 1. Breaking speed streaks
      // 2. Increasing cycle time (reducing time-efficiency points for next find)
      // 3. Lowering accuracy multiplier (reward system, not penalty)
      const levelScore = ipBreakdown.finalScore;

      // Create the level result
      const levelResult: LevelResult = {
        locationIndex: currentLocationIndex,
        locationName: currentLocation.name,
        score: levelScore,
        timeBonus,
        accuracy,
        foundEvidenceCount: evidenceBucket.foundEvidence.length,
        requiredEvidenceCount: actualRequiredEvidence,
        totalEvidenceCount: evidenceBucket.evidence.length,
        foundItems: foundEvidenceWithConclusions,
        highestSpeedStreak:
          evidenceBucket.foundEvidence.length > 0
            ? Math.max(highestSpeedStreak, 1)
            : 0,

        // IP scoring fields
        evidenceFinds,
        ipBreakdown,
        isNewPersonalBest: false, // Personal best tracking handled separately

        // Completion time tracking
        totalCompletionTime,
        firstFindTimestamp,
        lastFindTimestamp,
      };

      // Update the level results
      dispatch(addLevelResult(levelResult));

      saveService.completeLevel(currentLocation.id, ipBreakdown.finalScore, 0);

      // Track score earned for this level
      analytics.trackScoreEarned(levelScore.toString(), {
        base_evidence_points: ipBreakdown.evidencePoints,
        time_efficiency_bonus: ipBreakdown.timeEfficiencyPoints,
        speed_streak_multiplier: getStreakMultiplier(
          ipBreakdown.highestSpeedStreak,
        ),
        completeness_bonus: 0, // Not used in current scoring system
        accuracy_multiplier: ipBreakdown.accuracyMultiplier,
        total_score_earned: ipBreakdown.finalScore,
      });

      return levelResult;
    },
    [
      caseData,
      evidenceBucket,
      wrongClicks,
      wrongClickTimestamps,
      evidenceFinds,
      currentLocation,
      currentLocationIndex,
      gameConfig,
      dispatch,
      highestSpeedStreak,
    ],
  );

  /**
   * Calculates and sets the final game summary
   */
  const calculateAndSetFinalSummary = useCallback(() => {
    if (levelResults.length === 0) return;

    // Calculate overall accuracy
    const totalFound = levelResults.reduce(
      (sum, result) => sum + result.foundEvidenceCount,
      0,
    );
    const totalClicks = totalFound + wrongClicks || 1;
    const overallAccuracy = Math.round((totalFound / totalClicks) * 100);

    // Combine all found items
    const allFoundItems = levelResults.flatMap((result) => result.foundItems);

    // Calculate total completion time from first find to last find across all levels
    let totalCompletionTime = 0;
    const allTimestamps: number[] = [];
    levelResults.forEach((result) => {
      if (result.firstFindTimestamp)
        allTimestamps.push(result.firstFindTimestamp);
      if (result.lastFindTimestamp)
        allTimestamps.push(result.lastFindTimestamp);
    });
    if (allTimestamps.length > 0) {
      const firstFindEver = Math.min(...allTimestamps);
      const lastFindEver = Math.max(...allTimestamps);
      totalCompletionTime = (lastFindEver - firstFindEver) / 1000;
    }

    // Get time targets from caseData or use defaults
    const parTime = caseData?.parTime ?? DEFAULT_TIME_TARGETS.parTime;
    const aceTime = caseData?.aceTime ?? DEFAULT_TIME_TARGETS.aceTime;

    // Calculate pace multiplier based on total completion time
    const paceMultiplier = calculatePaceMultiplier(
      totalCompletionTime,
      parTime,
      aceTime,
    );

    // Calculate case pins based on total completion time
    const casePins = calculateCasePins(totalCompletionTime, parTime, aceTime);

    // Calculate total IP breakdown
    const scoreBeforePace = levelResults.reduce(
      (sum, result) =>
        sum +
        (result.ipBreakdown?.scoreBeforePace ||
          result.ipBreakdown?.subtotal ||
          0),
      0,
    );

    // Get highest speed streak across all levels
    const highestSpeedStreak = Math.max(
      ...levelResults.map(
        (result) => result.ipBreakdown?.highestSpeedStreak || 0,
      ),
    );

    const totalIPBreakdown: IPScoreBreakdown = {
      evidencePoints: levelResults.reduce(
        (sum, result) => sum + (result.ipBreakdown?.evidencePoints || 0),
        0,
      ),
      timeEfficiencyPoints: levelResults.reduce(
        (sum, result) => sum + (result.ipBreakdown?.timeEfficiencyPoints || 0),
        0,
      ),
      highestSpeedStreak,
      subtotal: levelResults.reduce(
        (sum, result) => sum + (result.ipBreakdown?.subtotal || 0),
        0,
      ),
      accuracyMultiplier: calculateAccuracyMultiplier(totalFound, wrongClicks),
      paceMultiplier,
      scoreBeforePace,
      totalCompletionTime,
      finalScore: cumulativeScore,
    };

    // Check if any level had a new personal best
    const isNewGlobalBest = levelResults.some(
      (result) => result.isNewPersonalBest,
    );

    // Get highest speed streak from the last played location
    const lastLocationHighestSpeedStreak =
      levelResults[levelResults.length - 1]?.highestSpeedStreak || 0;

    // Create the final game result
    const gameResult: GameResult = {
      totalScore: cumulativeScore,
      finalTimeBonus: levelResults[levelResults.length - 1]?.timeBonus || 0,
      overallAccuracy,
      levelResults,
      foundItems: allFoundItems,
      highestSpeedStreak: lastLocationHighestSpeedStreak,
      totalEvidenceCount: evidenceBucket?.evidence.length || 0,
      // IP scoring fields
      totalIPBreakdown,
      isNewGlobalBest,
      // New scoring V2 fields
      totalCompletionTime,
      paceMultiplier,
      casePins,
    };

    dispatch(setLastGameResult(gameResult));

    // Mark the case/episode as complete for episodic progression tracking
    if (caseData?.caseId) {
      saveService.completeLevel(caseData.caseId, cumulativeScore, 0);
    }
  }, [
    levelResults,
    wrongClicks,
    cumulativeScore,
    evidenceBucket,
    caseData,
    dispatch,
  ]);

  /**
   * Resets the evidence bucket and other state for the current location
   * Used when retrying a level
   */
  const resetCurrentLevel = useCallback(() => {
    if (
      !caseData ||
      !caseData.locations ||
      currentLocationIndex >= caseData.locations.length
    ) {
      return;
    }

    // Create a new evidence bucket for this location
    dispatch(initializeLocation());

    // Reset stats
    dispatch(resetScoring());
    dispatch(resetOverlays());

    // Reset game timer reference
    gameStartTimeRef.current = Date.now();
  }, [
    caseData,
    currentLocationIndex, // Reset stats
    dispatch,
  ]);

  // Initialize the first location after briefing
  useEffect(() => {
    if (gameStatus === GameStatus.GAMEPLAY && caseData && !evidenceBucket) {
      moveToLocation(currentLocationIndex);
    }
  }, [
    gameStatus,
    caseData,
    evidenceBucket,
    currentLocationIndex,
    moveToLocation,
  ]);

  // Update current IP score whenever evidence is found
  useEffect(() => {
    if (evidenceFinds.length > 0) {
      updateCurrentIPScore();
    }
  }, [evidenceFinds, updateCurrentIPScore]);

  return {
    // Game flow functions
    startNewGame,
    returnToTitle,
    advanceToNextLocation,
    resetCurrentLevel,

    // Scene unlock functions
    checkAndUnlockScenes,

    // Summary functions
    calculateAndSetLevelSummary,
    calculateAndSetFinalSummary,

    // IP scoring functions
    updateCurrentIPScore,

    // Bonus streak functions
    updateBonusStreak,
    breakStreak,

    // Other functions
    recordWrongClick,
  };
};
